/* eslint @typescript-eslint/no-explicit-any: 0 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';
import { Select } from 'formik-antd';
import { getIn, useFormikContext } from 'formik';
import Container from '../Container';
import { ANT_SELECT_HIDDEN_DROPDOWN_IF_EMPTY_CLASS_NAME } from '@libs/theme';
import debounce from 'lodash/debounce';
/*
Why do we need this?
:: Ant's autocomplete component is meant to be a text input which "predicts" and offers suggestions to fill in the text. Is is
not a component where the user is required to the the action of selecting an item but rather he has the option to do so.

:: Considering our use case of address autocomplete we actually do want the user to select an item from the dropdown
otherwise we would not be able to properly validate his input via formik and show and error in case he did not

:: Based on this thread here
https://github.com/ant-design/ant-design/issues/21882
it is indeed recommended to use a Select instead with some prop tweaking

:: An option whose data carries `__internal_refine` is a step on the way to an address rather than an address: a street
or a house number from the Danish address service. Picking one makes its text the search text and runs the search again
at once, with the list kept open on the next level, so the user can pick again or type on. The field is put back to what
it held, since nothing has been chosen yet. A Select closes its list and clears its search text after every pick, which
is why both are controlled here.
*/
const SelectAutocomplete = ({ name, loading, onChange, onSearch, onSelect, ...restProps }) => {
    const formik = useFormikContext();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    // The list closes once after every pick; a narrowing pick refuses that one close.
    const shouldRefuseNextCloseRef = useRef(false);
    // formik-antd writes every pick into the field before anything here runs, so a narrowing pick restores this.
    const fieldValueBeforePickRef = useRef();
    fieldValueBeforePickRef.current = getIn(formik === null || formik === void 0 ? void 0 : formik.values, name);
    const onSelectChange = useCallback((value, option) => {
        var _a;
        if ((_a = option === null || option === void 0 ? void 0 : option.data) === null || _a === void 0 ? void 0 : _a.__internal_refine) {
            return;
        }
        if (formik) {
            formik.setFieldValue(name, value);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(value, option);
    }, [name, formik, onChange]);
    const debounceOnChange = debounce(onSelectChange, 250);
    const debouncedOnSearch = useMemo(() => (typeof onSearch === 'function' ? debounce(onSearch, 250) : undefined), [onSearch]);
    const refineSearch = (text) => {
        shouldRefuseNextCloseRef.current = true;
        setSearchValue(text);
        if (formik) {
            formik.setFieldValue(name, fieldValueBeforePickRef.current);
        }
        onSearch === null || onSearch === void 0 ? void 0 : onSearch(text);
    };
    return (React.createElement(Select, Object.assign({ name: name, showSearch: true, showArrow: false, open: isDropdownOpen, searchValue: searchValue, onDropdownVisibleChange: open => {
            if (!open && shouldRefuseNextCloseRef.current) {
                shouldRefuseNextCloseRef.current = false;
                return;
            }
            setIsDropdownOpen(open);
        }, notFoundContent: loading ? (React.createElement(Container, { display: 'flex', justifyContent: 'center' },
            React.createElement(Spin, null))) : undefined, dropdownClassName: !loading ? ANT_SELECT_HIDDEN_DROPDOWN_IF_EMPTY_CLASS_NAME : undefined, filterOption: false, onSearch: text => {
            setSearchValue(text);
            debouncedOnSearch === null || debouncedOnSearch === void 0 ? void 0 : debouncedOnSearch(text);
        }, onSelect: (value, option) => {
            var _a, _b;
            // Select fires this for every pick, while onChange stays silent when the same value is picked again
            if ((_a = option === null || option === void 0 ? void 0 : option.data) === null || _a === void 0 ? void 0 : _a.__internal_refine) {
                refineSearch((_b = option.data.text) !== null && _b !== void 0 ? _b : String(value));
            }
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(value, option);
        }, onChange: debounceOnChange, onBlur: formik.handleBlur }, restProps)));
};
export default SelectAutocomplete;
